/**
 * AboutAuthorPanel — post-detail right-rail card showing the post author + Follow button.
 *
 * Uses an inner-component split (AboutAuthorPanelInner) to ensure hooks (useFollow,
 * useRequireAuth) are always called unconditionally, per the Rules of Hooks.
 */
import styled from 'styled-components';
import { Panel, SectionLabel, Avatar, Button } from '@/ui';
import { Icon } from '@/icons';
import { useFollow, useRequireAuth, followButtonLabel } from '@/hooks';
import { formatCount } from '@/components/formatCount';
import type { User } from '@/core/models';

/* ── Styled helpers ───────────────────────────────────────────────────────── */

const AuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AuthorInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Name = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 700;
  color: ${({ theme }) => theme.color.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Handle = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Bio = styled.p`
  margin: 10px 0 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  line-height: 1.4;
`;

const RoleLabel = styled.span`
  display: inline-block;
  margin-top: 6px;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.brandFrom};
  font-weight: 600;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  row-gap: 8px;
  column-gap: 0.9em;
  margin-top: 10px;
  flex-wrap: wrap;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const FollowButtonWrap = styled.div`
  margin-top: 14px;
`;

const Fallback = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

/* ── Inner component — always calls hooks unconditionally ─────────────────── */

function AboutAuthorPanelInner({ author }: { author: User }) {
  const { isFollowing, isRequested, toggleFollow, followerCountDelta } = useFollow(author.handle);
  const requireAuth = useRequireAuth();

  function handleFollow() {
    if (requireAuth('follow')) void toggleFollow();
  }

  const showFollowerCount = typeof author.followerCount === 'number';
  const showJoined = Boolean(author.joined);
  const liveFollowerCount = showFollowerCount
    ? (author.followerCount as number) + followerCountDelta
    : null;

  return (
    <>
      <AuthorRow>
        <Avatar name={author.name} size={56} />
        <AuthorInfo>
          <NameRow>
            <Name>{author.name}</Name>
            {author.verified && <Icon name="verify" size={15} title="Verified" />}
          </NameRow>
          <Handle>{author.handle}</Handle>
        </AuthorInfo>
      </AuthorRow>

      {author.bio && <Bio>{author.bio}</Bio>}
      {author.role && <RoleLabel>{author.role}</RoleLabel>}

      {(showFollowerCount || showJoined) && (
        <MetaRow>
          {showFollowerCount && liveFollowerCount !== null && (
            <span>
              <b>{formatCount(liveFollowerCount)}</b> Followers
            </span>
          )}
          {showJoined && <span>Joined {author.joined}</span>}
        </MetaRow>
      )}

      <FollowButtonWrap>
        <Button
          variant={isFollowing || isRequested ? 'secondary' : 'outline'}
          onClick={handleFollow}
          aria-label={`${followButtonLabel(isFollowing, isRequested)} ${author.handle}`}
        >
          {followButtonLabel(isFollowing, isRequested)}
        </Button>
      </FollowButtonWrap>
    </>
  );
}

/* ── Public panel component ───────────────────────────────────────────────── */

export interface AboutAuthorPanelProps {
  author: User | null;
}

export function AboutAuthorPanel({ author }: AboutAuthorPanelProps) {
  return (
    <Panel>
      <SectionLabel as="h2">About the author</SectionLabel>
      {author === null ? (
        <Fallback>Author info unavailable.</Fallback>
      ) : (
        <AboutAuthorPanelInner author={author} />
      )}
    </Panel>
  );
}
